import * as React from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";

/**
 * Inline read-only "copy to clipboard" widget for a tracker URL.
 *
 * The URL is shown as plain monospace text (so it's screen-reader-
 * friendly + selectable for keyboard users) with a copy button to
 * the right. Falls back to no-op silently if `navigator.clipboard`
 * is missing (older browsers, http-only contexts) — the URL is
 * always selectable manually as a backup.
 */
export function CopyableLink({ href, display }: { href: string; display: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(display);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Permissions denied or non-secure context — silently no-op.
      // The URL is still selectable in the input below as a fallback.
    }
  }, [display]);

  return (
    <div className="bg-background border-border/60 flex items-center gap-2 rounded-md border p-1.5">
      <input
        type="text"
        readOnly
        value={display}
        // Auto-selects on focus so a parent who taps the field without
        // hitting "Copy" can still ⌘+C / Ctrl+C the URL out manually.
        onFocus={(e) => e.currentTarget.select()}
        aria-label="Tracker link"
        className="text-foreground placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent px-2 py-1 font-mono text-xs focus:outline-none"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void handleCopy()}
        aria-label={copied ? "Tracker link copied" : "Copy tracker link"}
      >
        {copied ? (
          <>
            <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-500" />
            Copied
          </>
        ) : (
          <>
            <Copy className="mr-1.5 h-3.5 w-3.5" />
            Copy
          </>
        )}
      </Button>
      <Button asChild type="button" variant="outline" size="sm">
        <a href={href}>Open</a>
      </Button>
    </div>
  );
}
